import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    //old Users, Upload, Search, Loader2, FileText, CalendarRange, ClipboardCheck, Lock, KeyRound, X, Info, Download, FileSpreadsheet,
    Users, Upload, Search, Loader2, FileText, CalendarRange, ClipboardCheck, Lock, KeyRound, X, Info, Download, FileSpreadsheet, UserPlus,
} from "lucide-react";
import * as XLSX from "xlsx";
import api from "../../api/api";
import PendingInvites from "./PendingInvites";
import { InviteCode, inviteLink } from "./OrgPrograms";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import {
    Alert, EmptyState, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles,
} from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

// Same limit as the backend (org_service.MAX_BULK_IMPORT)
const MAX_IMPORT = 500;

/** A roster row is one person: an uploaded entry (RosterEntryID), or a member
 *  who joined another way and has no entry (ClientID only). */
const rowId = (row) => (row.RosterEntryID ? String(row.RosterEntryID) : `c${row.ClientID}`);

/** POST /licenses body for a row: its roster entry, or the account of a member without one. */
const licenseBody = (row) => (row.RosterEntryID
    ? { licenseType: "student", rosterEntryId: row.RosterEntryID }
    : { licenseType: "student", clientId: row.ClientID });

/** Removing a row: by roster entry (a linked account leaves the org too), or by
 *  account for a member without an entry. */
const removePath = (orgId, row) => (row.RosterEntryID
    ? `/org/${orgId}/roster/${row.RosterEntryID}`
    : `/org/${orgId}/students/${row.ClientID}`);

/** "First Last", or the email of someone added by email alone (the Overview's Assign a license), who has no
 *  name yet. (Also used by the Licenses page.) */
export const personName = (row) => (row.FirstName || row.LastName ? `${row.FirstName} ${row.LastName}` : row.Email || row.EmailAddress || "");

const removeConfirm = (row) => (row.Status === "invited"
    //old ? `Remove ${row.FirstName} ${row.LastName} from your roster?`
    ? `Remove ${personName(row)} from your roster?`
        + (row.HasLicense ? " Their license code stops working now. The license stays used until next month, so it can't go to someone else this month." : "")
    //old : `Remove ${row.FirstName} ${row.LastName} from your organization? Their program enrollments end and any license is removed.`);
    : `Remove ${personName(row)} from your organization? Their program enrollments end and any license is removed.`);

/** Giving a license uses one of this month's seats at once, so a mis-click is asked about first. */
const assignConfirm = (person) => {
    //old const name = [person?.FirstName, person?.LastName].filter(Boolean).join(" ") || "this student";
    const name = [person?.FirstName, person?.LastName].filter(Boolean).join(" ") || "this client";
    //old return `Give ${name} a student license? It counts against this month's licenses right away. If you remove it later, the seat stays used until next month.`;
    return `Give ${name} a client license? It counts against this month's licenses right away. If you remove it later, the seat stays used until next month.`;
};

/** Columns of the "Download codes" file. */
const CODE_EXPORT_COLUMNS = ["First name", "Last name", "Roster ID", "Email", "Program", "Status", "License code", "Join link"];

export default function OrgStudents() {
    const { orgId, org, isOrgAdmin, reloadOrg } = useOrgPortal();
    const s = useOrgStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const [students, setStudents] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [importOpen, setImportOpen] = useState(false);
    // Add client's window, and what it did once it closed
    const [addOpen, setAddOpen] = useState(false);
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const programId = searchParams.get("programId") || "";
    //old const sort = searchParams.get("sort") === "program" ? "program" : "alpha";
    // Status first (Alicia, Oct 30; JJ, Nov 1): with no sort in the URL, clients without a license this month come first
    const sort = ["alpha", "program"].includes(searchParams.get("sort")) ? searchParams.get("sort") : "license";
    const openStudentId = Number(searchParams.get("student")) || null;
    // Someone who hasn't joined yet has no account; they open by roster entry. With
    // student= it's the roster entry of the student being viewed.
    const openEntryId = Number(searchParams.get("entry")) || null;

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value); else next.delete(key);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 250);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ sort });
            if (programId) params.set("programId", programId);
            if (debounced) params.set("search", debounced);
            const res = await api.get(`/org/${orgId}/roster?${params}`);
            //old setStudents(res.data.students || []);
            setStudents(res.data.roster || []);
        } catch (err) {
            //old setError(apiError(err, "Couldn't load students."));
            setError(apiError(err, "Couldn't load clients."));
        } finally {
            setLoading(false);
        }
    }, [orgId, programId, sort, debounced]);

    const loadInvites = useCallback(async () => {
        if (!isOrgAdmin) return;
        try {
            const res = await api.get(`/org/${orgId}/invites`);
            setInvites(res.data.invites || []);
        } catch { setInvites([]); }
    }, [orgId, isOrgAdmin]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get(`/org/${orgId}/programs`).then((res) => setPrograms(res.data.programs || [])).catch(() => setPrograms([]));
        loadInvites();
    }, [orgId, loadInvites]);

    const refreshAll = async () => { await Promise.all([load(), loadInvites(), reloadOrg()]); };

    // Row actions (assign a license, remove)
    const act = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await refreshAll();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };
    const assignLicense = (row) => {
        if (window.confirm(assignConfirm(row))) act(() => api.post(`/org/${orgId}/licenses`, licenseBody(row)));
    };
    const removeRow = (row) => {
        if (window.confirm(removeConfirm(row))) act(() => api.delete(removePath(orgId, row)));
    };
    // Several URL params in one update (two setParam calls in a row would overwrite each other)
    const setParams = (values) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(values).forEach(([key, value]) => { if (value) next.set(key, value); else next.delete(key); });
        setSearchParams(next, { replace: true });
    };
    // Joined members open the student view (by account); invited people the roster entry.
    // A joined row's entry goes in the URL too (entry= next to student=), so the view
    // keeps it after the row leaves this filtered list (e.g. removed from the program).
    const openRow = (row) => (row.Status === "invited"
        ? setParams({ student: "", entry: row.RosterEntryID })
        : setParams({ student: row.ClientID, entry: row.RosterEntryID }));
    // entry= without student= is someone who hasn't joined yet
    const openEntry = useMemo(() => (openEntryId && !openStudentId ? students.find((r) => r.RosterEntryID === openEntryId) || null : null),
        [students, openEntryId, openStudentId]);

    // Excel file of the people in this view (program filter and search), with each license code and join link
    const downloadCodes = async () => {
        setError("");
        try {
            const params = new URLSearchParams({ sort: "alpha" });
            if (programId) params.set("programId", programId);
            if (debounced) params.set("search", debounced);
            const res = await api.get(`/org/${orgId}/roster?${params}`);
            const rows = (res.data.roster || []).map((r) => {
                const code = r.HasLicense && r.LicenseCode ? r.LicenseCode : "";
                return {
                    "First name": r.FirstName || "",
                    "Last name": r.LastName || "",
                    "Roster ID": r.RosterID || "",
                    Email: r.Email || "",
                    Program: (r.Programs || []).map((p) => p.ProgramName).join(", "),
                    Status: r.Status === "invited" ? "Invited" : "Joined",
                    "License code": code,
                    "Join link": code ? inviteLink(code) : "",
                };
            });
            const sheet = XLSX.utils.json_to_sheet(rows, { header: CODE_EXPORT_COLUMNS });
            sheet["!cols"] = [{ wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 30 }, { wch: 24 }, { wch: 10 }, { wch: 16 }, { wch: 48 }];
            const book = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(book, sheet, "License codes");
            XLSX.writeFile(book, `License_Codes_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            setError(apiError(err, "Couldn't download the codes."));
        }
    };

    return (
        <div className="space-y-6">
            <Alert onClose={() => setError("")}>{error}</Alert>
            {/* a long email in it wraps inside the box on a phone (as the Overview's Assign a license messages) */}
            <Alert kind="success" onClose={() => setNotice("")}>{notice && <span className="[overflow-wrap:anywhere]">{notice}</span>}</Alert>
            <Panel
                //old title="Student roster"
                title="Client roster"
                padded={false}
                actions={isOrgAdmin && (
                    //old <button type="button" className={s.btnPrimary} onClick={() => setImportOpen(true)}><Upload size={16} /> Import students</button>
                    <>
                        <button type="button" className={s.btnSecondary} onClick={downloadCodes} disabled={students.length === 0}>
                            <Download size={16} /> Download codes
                        </button>
                        {/* one at a time, for an organization with no roster file (Alicia, Oct 30) */}
                        <button type="button" className={s.btnSecondary} onClick={() => { setNotice(""); setAddOpen(true); }}>
                            <UserPlus size={16} /> Add client
                        </button>
                        <button type="button" className={s.btnPrimary} onClick={() => setImportOpen(true)}><Upload size={16} /> Upload roster</button>
                    </>
                )}
            >
                <div className={`flex flex-col gap-3 border-b px-5 py-4 md:flex-row md:items-center ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                    <div className="relative flex-1">
                        <Search size={16} className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${s.muted}`} />
                        <input
                            //old aria-label="Search students"
                            aria-label="Search clients"
                            //old placeholder="Search by name or email"
                            placeholder="Search by name, roster ID or email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={`${s.input} pl-9`}
                        />
                    </div>
                    <select aria-label="Filter by program" value={programId} onChange={(e) => setParam("programId", e.target.value)} className={`${s.input} md:w-56`}>
                        <option value="">All programs</option>
                        {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                    </select>
                    {/* old <select aria-label="Sort" value={sort} onChange={(e) => setParam("sort", e.target.value === "alpha" ? "" : e.target.value)} className={`${s.input} md:w-48`}> */}
                    {/* the default (no license first) stays out of the URL, as A–Z did */}
                    <select aria-label="Sort" value={sort} onChange={(e) => setParam("sort", e.target.value === "license" ? "" : e.target.value)} className={`${s.input} md:w-48`}>
                        <option value="license">Sort: no license first</option>
                        <option value="alpha">Sort: A–Z</option>
                        <option value="program">Sort: by program</option>
                    </select>
                </div>
                {loading ? (
                    //old <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                    <InlineTableSkeleton isDarkMode={s.dark} rows={6} cols={7} bordered={false} />
                ) : students.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        //old title={debounced || programId ? "No matching students" : "No students yet"}
                        title={debounced || programId ? "No matching clients" : "No clients yet"}
                        //old body={debounced || programId ? "Try a different search or program." : "Share a program's invitation code, or import students by email."}
                        //old body={debounced || programId ? "Try a different search or program." : "Upload your roster to give each student a license code, or share a program's invitation code."}
                        // only org admins upload rosters, so coaches just get the title
                        //old body={debounced || programId ? "Try a different search or program." : isOrgAdmin ? "Upload your roster to give each student a license code, or share a program's invitation code." : null}
                        // (a program has no invitation code any more, Alicia, Oct 30)
                        body={debounced || programId ? "Try a different search or program." : isOrgAdmin ? "Upload your roster to give each client a license code." : null}
                    />
                ) : (
                    //old <RosterTable students={students} sort={sort} onOpen={(id) => setParam("student", id)} />
                    <RosterGrid rows={students} sort={sort} isOrgAdmin={isOrgAdmin} busy={busy}
                                onOpen={openRow} onAssign={assignLicense} onRemove={removeRow} />
                )}
            </Panel>

            {isOrgAdmin && (
                <PendingInvites orgId={orgId} invites={invites.filter((i) => i.InviteType !== "coach")}
                                intro="These emails don't have a Pono Pilot account yet. Each one was emailed an invitation link; they join automatically when they sign up with that email. If the email didn't arrive, use Copy link and send it yourself. Invites expire after 30 days — Resend or Copy link starts them over."
                                onChanged={loadInvites} onError={setError} />
            )}

            {importOpen && (
                //old <ImportModal orgId={orgId} org={org} programs={programs} defaultProgramId={programId}
                //old              onClose={() => setImportOpen(false)} onDone={refreshAll} />
                <RosterImportModal orgId={orgId} org={org} programs={programs} defaultProgramId={programId}
                                   onClose={() => setImportOpen(false)} onDone={refreshAll} />
            )}
            {addOpen && (
                <AddClientModal orgId={orgId} org={org} programs={programs} defaultProgramId={programId}
                                onClose={() => setAddOpen(false)}
                                onDone={async (message) => { setNotice(message); await refreshAll(); }} />
            )}
            {openStudentId && (
                <StudentDetail orgId={orgId} clientId={openStudentId} isOrgAdmin={isOrgAdmin} programs={programs}
                               rosterEntryId={openEntryId}
                               //old onClose={() => setParam("student", "")} onChanged={refreshAll} />
                               onClose={() => setParams({ student: "", entry: "" })} onChanged={refreshAll} />
            )}
            {openEntry && (
                <RosterEntryDetail orgId={orgId} entry={openEntry} isOrgAdmin={isOrgAdmin}
                                   onClose={() => setParam("entry", "")} onChanged={refreshAll} />
            )}
        </div>
    );
}

function SharedIcons({ student }) {
    const s = useOrgStyles();
    const items = [
        { on: student.ShareResume, icon: FileText, label: "Resume" },
        { on: student.ShareQuarterlyResults, icon: CalendarRange, label: "Quarterly results" },
        { on: student.ShareProgramProgress, icon: ClipboardCheck, label: "Program progress" },
    ];
    return (
        <span className="inline-flex items-center gap-1.5">
            {items.map(({ on, icon: Icon, label }) => (
                <span key={label} title={`${label}: ${on ? "shared" : "not shared"}`} aria-label={`${label}: ${on ? "shared" : "not shared"}`}>
                    <Icon size={15} className={on ? (s.dark ? "text-primary-400" : "text-primary-600") : (s.dark ? "text-slate-600" : "text-slate-300")} />
                </span>
            ))}
        </span>
    );
}

// Replaced by RosterGrid (roster entries, status, license codes).
//old function RosterTable({ students, sort, onOpen }) {
//old     const s = useOrgStyles();
//old     return (
//old         <div className="relative overflow-x-auto">
//old             <table className="w-full">
//old                 <thead>
//old                     <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
//old                         <th className={s.th}>Student</th>
//old                         <th className={s.th}>{sort === "program" ? "Program" : "Programs"}</th>
//old                         <th className={s.th}>License</th>
//old                         <th className={s.th}>Shared</th>
//old                         <th className={s.th}><span className="sr-only">Open</span></th>
//old                     </tr>
//old                 </thead>
//old                 <tbody>
//old                     {students.map((st, i) => (
//old                         <tr key={`${st.ClientID}-${st.ProgramID || i}`} className={`border-b last:border-0 ${s.row}`}>
//old                             <td className={s.td}>
//old                                 <button type="button" onClick={() => onOpen(st.ClientID)} className={`text-left font-semibold ${s.text} hover:underline`}>
//old                                     {st.LastName}, {st.FirstName}
//old                                 </button>
//old                                 <div className={`text-xs ${s.muted}`}>{st.EmailAddress}</div>
//old                             </td>
//old                             <td className={s.td}>
//old                                 {sort === "program"
//old                                     ? (st.ProgramName || <span className={s.muted}>No program</span>)
//old                                     : (st.Programs || []).length
//old                                         ? <span className="flex flex-wrap gap-1">{st.Programs.map((p) => <Pill key={p.ProgramID}>{p.ProgramName}</Pill>)}</span>
//old                                         : <span className={s.muted}>No program</span>}
//old                             </td>
//old                             <td className={s.td}>{st.HasLicense ? <Pill tone="success">Licensed</Pill> : <span className={s.muted}>None</span>}</td>
//old                             <td className={s.td}><SharedIcons student={st} /></td>
//old                             <td className={`${s.td} text-right`}>
//old                                 <button type="button" className={s.btnLink} onClick={() => onOpen(st.ClientID)}>View</button>
//old                             </td>
//old                         </tr>
//old                     ))}
//old                 </tbody>
//old             </table>
//old         </div>
//old     );
//old }

/** The roster: uploaded people (invited until they redeem their license code)
 *  and members who joined another way. One row per person, or per program when
 *  sorted by program. Codes are only sent to org admins. */
/** An email that wraps after a dot of its name part or before the @ (not
 *  mid-word) when its column is narrow. Also used by the Licenses and staff tables. */
export function EmailText({ email }) {
    const at = email.lastIndexOf("@");
    if (at <= 0) return email;
    const out = [];
    email.slice(0, at).split(".").forEach((part, i) => {
        if (i) out.push(".", <wbr key={`dot${i}`} />);
        out.push(part);
    });
    out.push(<wbr key="at" />, email.slice(at));
    return out;
}

function RosterGrid({ rows, sort, isOrgAdmin, busy, onOpen, onAssign, onRemove }) {
    const s = useOrgStyles();
    // More columns than the other org tables, so the cells are a little tighter
    const th = s.th.replace("px-4", "px-3");
    const td = s.td.replace("px-4", "px-3");
    return (
        <div className="relative overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                        <th className={th}>Name</th>
                        <th className={th}>Roster ID</th>
                        <th className={th}>Email</th>
                        <th className={th}>{sort === "program" ? "Program" : "Programs"}</th>
                        <th className={th}>Status</th>
                        <th className={th}>License</th>
                        {isOrgAdmin && <th className={th}><span className="sr-only">Actions</span></th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const id = rowId(r);
                        const joined = r.Status !== "invited";
                        return (
                            //old <tr key={sort === "program" ? `${id}-${r.ProgramID || "none"}` : id} className={`border-b last:border-0 ${s.row}`}>
                            // keyed by the row itself (a by-program row has its program: one person can be in two), not by the sort,
                            // which changes before the new sort's rows arrive: someone in two programs stayed listed twice after it
                            <tr key={"ProgramID" in r ? `${id}-${r.ProgramID || "none"}` : id} className={`border-b last:border-0 ${s.row}`}>
                                <td className={td}>
                                    {/* one line once the window fits the whole grid; narrower windows wrap at the comma */}
                                    <button type="button" onClick={() => onOpen(r)} className={`text-left font-semibold min-[1320px]:whitespace-nowrap ${s.text} hover:underline`}>
                                        {/* old {r.LastName}, {r.FirstName} */}
                                        {/* someone added by email alone (the Overview's Assign a license) has no name yet: their email, wrapping */}
                                        {r.FirstName || r.LastName ? <>{r.LastName}, {r.FirstName}</>
                                            : <span className="inline-block min-w-[7rem] whitespace-normal [overflow-wrap:anywhere]"><EmailText email={r.Email || r.EmailAddress || ""} /></span>}
                                    </button>
                                </td>
                                <td className={`${td} whitespace-nowrap`}>{r.RosterID || <span className={s.muted}>—</span>}</td>
                                {/* long emails wrap instead of pushing the last columns out of view */}
                                <td className={`${td} min-w-[7rem] [overflow-wrap:anywhere]`}>
                                    {r.Email ? <EmailText email={r.Email} /> : <span className={s.muted}>—</span>}
                                    {r.AccountEmail && r.Email && r.AccountEmail.toLowerCase() !== r.Email.toLowerCase() && (
                                        <div className={`text-xs ${s.muted}`}>Signed in as <EmailText email={r.AccountEmail} /></div>
                                    )}
                                </td>
                                <td className={td}>
                                    {sort === "program"
                                        ? (r.ProgramName || <span className={s.muted}>No program</span>)
                                        : (r.Programs || []).length
                                            ? <span className="flex flex-wrap gap-1">{r.Programs.map((p) => <Pill key={p.ProgramID}>{p.ProgramName}</Pill>)}</span>
                                            : <span className={s.muted}>No program</span>}
                                </td>
                                <td className={td}>
                                    {/* what a joined student shares sits under their status (invited people share nothing yet) */}
                                    <div className="flex flex-col items-start gap-1.5">
                                        <Pill tone={joined ? "primary" : "neutral"}>{joined ? "Joined" : "Invited"}</Pill>
                                        {joined && <SharedIcons student={r} />}
                                    </div>
                                </td>
                                <td className={td}>
                                    {r.HasLicense ? (
                                        <div className="flex flex-col items-start gap-1">
                                            <Pill tone="success">Licensed</Pill>
                                            {/* the code stays on one line */}
                                            {r.LicenseCode && <span className="whitespace-nowrap"><InviteCode code={r.LicenseCode} /></span>}
                                            {r.LicenseCode && !r.LicenseRedeemed && <span className={`text-xs ${s.muted}`}>Not redeemed yet</span>}
                                        </div>
                                    ) : isOrgAdmin ? (
                                        <button type="button" className={s.btnSm} disabled={busy} onClick={() => onAssign(r)}>Assign license</button>
                                    ) : <span className={s.muted}>None</span>}
                                </td>
                                {/* the name opens the details (View); Assign sits in the License column */}
                                {isOrgAdmin && (
                                    <td className={`${td} text-right`}>
                                        <button type="button" className={s.btnDanger} disabled={busy} onClick={() => onRemove(r)}>Remove</button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// Replaced by RosterImportModal (Excel/CSV roster upload with license codes).
//old function ImportModal({ orgId, org, programs, defaultProgramId, onClose, onDone }) {
//old     const s = useOrgStyles();
//old     const [emails, setEmails] = useState("");
//old     const [programId, setProgramId] = useState(defaultProgramId || "");
//old     const [assignLicense, setAssignLicense] = useState(false);
//old     const [busy, setBusy] = useState(false);
//old     const [error, setError] = useState("");
//old     const [result, setResult] = useState(null);
//old     const count = useMemo(() => new Set(emails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)).size, [emails]);
//old     const tooMany = count > MAX_IMPORT;
//old
//old     const onFile = async (e) => {
//old         const file = e.target.files?.[0];
//old         if (!file) return;
//old         const text = await file.text();
//old         const found = text.match(/[^\s@,;"'<>]+@[^\s@,;"'<>]+\.[^\s@,;"'<>]+/g) || [];
//old         setEmails((prev) => [prev.trim(), ...found].filter(Boolean).join("\n"));
//old         e.target.value = "";
//old     };
//old
//old     const submit = async (e) => {
//old         e.preventDefault();
//old         setBusy(true);
//old         setError("");
//old         try {
//old             const res = await api.post(`/org/${orgId}/students/import`, {
//old                 emails, programId: programId ? Number(programId) : null, assignLicense,
//old             });
//old             setResult(res.data);
//old             await onDone();
//old         } catch (err) {
//old             setError(apiError(err));
//old         } finally {
//old             setBusy(false);
//old         }
//old     };
//old
//old     const studentSeats = org.StudentLicenseLimit + (org.StudentLicensesPurchased || 0);
//old     const seatsLeft = Math.max(0, studentSeats - org.StudentLicensesUsed);
//old
//old     return (
//old         <OrgModal
//old             isOpen
//old             wide
//old             onClose={onClose}
//old             title="Import students"
//old             footer={result
//old                 ? <button type="button" className={s.btnPrimary} onClick={onClose}>Done</button>
//old                 : <>
//old                     <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
//old                     <button type="submit" form="import-form" className={s.btnPrimary} disabled={busy || count === 0 || tooMany}>
//old                         {busy && <Loader2 size={14} className="animate-spin" />} Import {count || ""} email{count === 1 ? "" : "s"}
//old                     </button>
//old                 </>}
//old         >
//old             <Alert>{error}</Alert>
//old             {result ? (
//old                 <div className="space-y-3 text-sm">
//old                     <ResultLine label="Added to your roster" items={result.added} tone="success" />
//old                     <ResultLine label="Already on your roster" items={result.alreadyMember} />
//old                     <ResultLine label="Invited (no account yet; they join when they sign up)" items={result.invited} tone="primary" />
//old                     <ResultLine label="Skipped: already invited as a coach" items={result.invitedAsCoach} />
//old                     {result.invited?.length > 0 && (
//old                         <p className={s.muted}>
//old                             {result.emailed} invitation email{result.emailed === 1 ? "" : "s"} sent.
//old                             {result.emailed < result.invited.length && " Addresses that were already invited weren't emailed again. Use Resend under Pending invites."}
//old                         </p>
//old                     )}
//old                     <ResultLine label="Licenses assigned" items={result.licensed} tone="success" />
//old                     <ResultLine label="Not valid email addresses" items={result.invalid} tone="danger" />
//old                     {result.licenseErrors?.length > 0 && (
//old                         <div>
//old                             <div className={`font-semibold ${s.text}`}>Licenses not assigned ({result.licenseErrors.length})</div>
//old                             <p className={s.muted}>{result.licenseErrors[0].error}</p>
//old                         </div>
//old                     )}
//old                 </div>
//old             ) : (
//old                 <form id="import-form" onSubmit={submit} className="space-y-4">
//old                     <Field label="Emails" htmlFor="imp-emails" hint={`One per line, or separated by commas. You can also load a CSV file. Up to ${MAX_IMPORT} at a time.`}>
//old                         <textarea id="imp-emails" rows={8} className={`${s.input} font-mono`} value={emails}
//old                                   onChange={(e) => setEmails(e.target.value)} placeholder={"student1@school.edu\nstudent2@school.edu"} />
//old                         {tooMany && <p className="mt-1 text-xs font-medium text-red-600">That's {count} emails. Import up to {MAX_IMPORT} at a time.</p>}
//old                     </Field>
//old                     <label className={`inline-flex cursor-pointer items-center gap-2 ${s.btnSecondary}`}>
//old                         <Upload size={14} /> Load from CSV
//old                         <input type="file" accept=".csv,.txt,text/csv,text/plain" className="sr-only" onChange={onFile} />
//old                     </label>
//old                     <Field label="Add to program" htmlFor="imp-program">
//old                         <select id="imp-program" className={s.input} value={programId} onChange={(e) => setProgramId(e.target.value)}>
//old                             <option value="">Roster only (no program)</option>
//old                             {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
//old                         </select>
//old                     </Field>
//old                     {studentSeats > 0 && (
//old                         <label className={`flex items-start gap-2 text-sm ${s.body}`}>
//old                             <input type="checkbox" className="mt-1" checked={assignLicense} onChange={(e) => setAssignLicense(e.target.checked)} />
//old                             <span>
//old                                 Assign a student license to each student
//old                                 <span className={`block text-xs ${s.muted}`}>{seatsLeft} of {studentSeats} student licenses left this month. Existing accounts get one now; invited emails get one when they sign up, if a license is still free then.</span>
//old                             </span>
//old                         </label>
//old                     )}
//old                 </form>
//old             )}
//old         </OrgModal>
//old     );
//old }

// Lines read from an upload: far more than any roster needs, so blank or formatted
// rows below the students don't matter. A file that reaches this many is refused,
// never uploaded cut short.
const READ_ROWS = 100000;

// Upload headers, compared lowercased without spaces or punctuation ("First Name",
// "first_name" → "firstname"). When a sheet has two matches, the earlier name wins.
const ROSTER_COLUMNS = {
    firstName: ["firstname", "first", "givenname", "fname", "studentfirstname"],
    lastName: ["lastname", "last", "surname", "familyname", "lname", "studentlastname"],
    rosterId: ["rosterid", "studentid", "studentnumber", "studentno", "idnumber", "schoolid", "employeeid", "memberid", "id"],
    email: ["email", "emailaddress", "studentemail", "schoolemail", "mail"],
};
const headerKey = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const headerWords = (value) => String(value || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// The fields, named and ordered as the template and the upload preview show them
const ROSTER_FIELDS = [["firstName", "First name"], ["lastName", "Last name"], ["rosterId", "Roster ID"], ["email", "Email"]];

// Header words for someone other than the student, and for numbers that aren't a
// person's ID (a guessed column must name the student's own ID or email)
const NOT_THE_STUDENT = /parent|guardian|mother|father|emergency|contact/;
const NOT_AN_ID = /name|mail|phone|room|course|section|class|period|grade|locker|zip|postal/;
const ID_OWNERS = ["student", "roster", "school", "sis", "member", "learner", "pupil", "user", "person", "employee", "participant",
    "local", "state"];
// Student ID abbreviations used as a header on their own ("SID", "LASID")
const ID_ABBREVIATIONS = ["sid", "ssid", "lasid", "sasid"];

/** Column of each field in a header row: an exact name above, else a header that
 *  names the student's own field ("Student Email Address", "Legal First Name",
 *  "Student ID Number", "School ID No."), preferring one that says "student".
 *  One file column fills one field at most. `guessed` lists the fields found the
 *  second way; parseRoster checks their values before using them. */
function matchColumns(cells) {
    const keys = cells.map(headerKey);
    const found = {};
    const guessed = new Set();
    const used = new Set();
    const take = (field, index, guess) => {
        if (index < 0 || found[field] !== undefined || used.has(index)) return;
        found[field] = index;
        used.add(index);
        if (guess) guessed.add(field);
    };
    Object.entries(ROSTER_COLUMNS).forEach(([field, names]) => {
        const name = names.find((n) => keys.includes(n));
        if (name) take(field, keys.indexOf(name));
    });
    const contains = (field, test) => {
        const fits = (key, i) => key && !used.has(i) && !NOT_THE_STUDENT.test(key) && test(key, headerWords(cells[i]));
        take(field, keys.findIndex((key, i) => fits(key, i) && key.includes("student")), true);
        take(field, keys.findIndex(fits), true);
    };
    contains("email", (key) => key.includes("email"));
    contains("firstName", (key) => key.includes("first") || key.includes("given"));
    contains("lastName", (key) => key.includes("last") || key.includes("surname") || key.includes("family"));
    // A student ID abbreviation, or an ID / number word next to whose it is:
    // "Student ID Number", "Local ID", "Employee No"
    contains("rosterId", (key, words) => words.some((w) => ID_ABBREVIATIONS.includes(w)) && !NOT_AN_ID.test(key));
    ["id", "number", "num", "no"].forEach((word) => contains("rosterId", (key, words) => words.includes(word)
        && ID_OWNERS.some((owner) => key.includes(owner)) && !NOT_AN_ID.test(key)));
    return { found, guessed };
}

/** Why a guessed column can't be used (null when it can): a roster ID or email
 *  column holds each student's own value, and an email column holds emails.
 *  `values` is [{ value, person, sheetRow }]. The same student on two rows is fine
 *  (the upload treats the second row as a repeat); two students with one value
 *  means the column isn't theirs, like a "School ID" that's the same on every row. */
function guessedColumnProblem(field, values) {
    if (values.length === 0) return null;
    const firstWith = new Map(); // value → its first row
    for (const v of values) {
        const first = firstWith.get(v.value.toLowerCase());
        if (!first) firstWith.set(v.value.toLowerCase(), v);
        //old else if (first.person !== v.person) return `different students have the same value (rows ${first.sheetRow} and ${v.sheetRow})`;
        else if (first.person !== v.person) return `different clients have the same value (rows ${first.sheetRow} and ${v.sheetRow})`;
    }
    if (field === "email" && values.filter((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.value)).length < values.length * 0.8) {
        return "it doesn't hold email addresses";
    }
    return null;
}

/** The read stopped at READ_ROWS with more of the sheet after it. SheetJS keeps a
 *  cut sheet's whole range in !fullref; a CSV just stops, so reaching the last line
 *  read counts as cut too. */
function readCutShort(sheet) {
    const read = XLSX.utils.decode_range(sheet["!ref"]);
    if (sheet["!fullref"] && XLSX.utils.decode_range(sheet["!fullref"]).e.r > read.e.r) return true;
    return read.e.r >= READ_ROWS - 1;
}

/** A cell as text. Numbers keep every digit (Excel shows a long ID as 1.23E+12)
 *  and the zero padding of their number format ("000123"). */
function cellText(cell) {
    if (!cell || cell.v == null) return "";
    if (cell.t === "n") return /^\d+$/.test(cell.w || "") ? cell.w : String(cell.v);
    return String(cell.v).trim();
}

/** A CSV saved by Excel is UTF-8, or the older Windows encoding. */
function decodeCsv(buffer) {
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch {
        return new TextDecoder("windows-1252").decode(buffer);
    }
}

/** First sheet → { rows, missing, headers, ignored, repeats, truncated }: finds the header row (in the
 *  first 10 rows), then one person per line. Lines without a first or last name go
 *  to `missing`. sheetRow is the line number people see in Excel; headers is the
 *  file's column name for each field found; truncated means the file goes on past
 *  what was read. */
function parseRoster(book) {
    const sheet = book.Sheets[book.SheetNames[0]];
    if (!sheet || !sheet["!ref"]) return { error: "That file is empty." };
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const lastCol = Math.min(range.e.c, range.s.c + 50);
    const lines = [];
    for (let r = range.s.r; r <= range.e.r; r += 1) {
        const cells = [];
        for (let c = range.s.c; c <= lastCol; c += 1) cells.push(cellText(sheet[XLSX.utils.encode_cell({ r, c })]));
        lines.push({ sheetRow: r + 1, cells });
    }
    let columns = null;
    let guessed = null;
    let headerCells = null;
    let start = 0;
    for (let i = 0; i < Math.min(lines.length, 10) && !columns; i += 1) {
        const match = matchColumns(lines[i].cells);
        if (match.found.firstName !== undefined && match.found.lastName !== undefined) {
            ({ found: columns, guessed } = match);
            headerCells = lines[i].cells;
            start = i + 1;
        }
    }
    if (!columns) {
        return { error: "We couldn't find First name and Last name columns. Put headers like First name, Last name, Roster ID and Email in the first row, or start from the template." };
    }
    // A guessed roster ID or email column is used only if its values fit
    const ignored = []; // [{ label, header, reason }]
    ["rosterId", "email"].filter((field) => guessed.has(field)).forEach((field) => {
        const values = lines.slice(start).map(({ sheetRow, cells }) => ({
            value: cells[columns[field]] || "",
            person: `${cells[columns.firstName] || ""} ${cells[columns.lastName] || ""}`.trim().toLowerCase(),
            sheetRow,
        })).filter((v) => v.value);
        const reason = guessedColumnProblem(field, values);
        if (reason) {
            ignored.push({ label: ROSTER_FIELDS.find(([f]) => f === field)[1], header: headerCells[columns[field]], reason });
            delete columns[field];
        }
    });
    const headers = {};
    Object.entries(columns).forEach(([field, index]) => { headers[field] = headerCells[index]; });
    const rows = [];
    const missing = [];
    lines.slice(start).forEach(({ sheetRow, cells }) => {
        const get = (field) => (columns[field] === undefined ? "" : cells[columns[field]] || "");
        const row = { sheetRow, firstName: get("firstName"), lastName: get("lastName"), rosterId: get("rosterId"), email: get("email") };
        if (!row.firstName && !row.lastName && !row.rosterId && !row.email) return; // blank line
        if (row.firstName && row.lastName) rows.push(row); else missing.push(row);
    });
    //old if (rows.length === 0 && missing.length === 0) return { error: "There are no students in that file." };
    if (rows.length === 0 && missing.length === 0) return { error: "There are no clients in that file." };
    // Rows whose roster ID or email an earlier row already has: uploaded once, as the same student
    const seen = new Set();
    const repeats = rows.filter((r) => {
        const keys = [r.rosterId && `id:${r.rosterId.toLowerCase()}`, r.email && `email:${r.email.toLowerCase()}`].filter(Boolean);
        const again = keys.some((k) => seen.has(k));
        keys.forEach((k) => seen.add(k));
        return again;
    }).map((r) => r.sheetRow);
    return { rows, missing, headers, ignored, repeats, truncated: readCutShort(sheet) };
}

/** Empty upload file with the headers the upload looks for. */
function downloadRosterTemplate() {
    const sheet = XLSX.utils.aoa_to_sheet([["First name", "Last name", "Roster ID", "Email"]]);
    sheet["!cols"] = [{ wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 30 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Roster");
    XLSX.writeFile(book, "Roster_Template.xlsx");
}

/**
 * Upload a roster from Excel or CSV: First name, Last name, and optionally the
 * org's own Roster ID and Email. Everyone becomes an invited roster entry; with
 * the checkbox, everyone in the file without a license this month (people already
 * on the roster too) gets one (and its code) in file order while seats last.
 */
function RosterImportModal({ orgId, org, programs, defaultProgramId, onClose, onDone }) {
    const s = useOrgStyles();
    const [fileName, setFileName] = useState("");
    const [parsed, setParsed] = useState(null); // { rows, missing, headers, ignored, repeats, truncated }
    const [programId, setProgramId] = useState(defaultProgramId || "");
    const [assignLicense, setAssignLicense] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const rows = parsed?.rows || [];
    // Over the limit, or a file that goes on past the lines read (never uploaded in part)
    const tooMany = rows.length > MAX_IMPORT || !!parsed?.truncated;

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // the same file can be picked again after fixing it
        if (!file) return;
        setError("");
        setParsed(null);
        setFileName(file.name);
        try {
            // CSV is read as text so every cell stays as typed (leading zeros, accents)
            const book = /\.csv$/i.test(file.name)
                ? XLSX.read(decodeCsv(await file.arrayBuffer()), { type: "string", raw: true, sheetRows: READ_ROWS })
                : XLSX.read(await file.arrayBuffer(), { type: "array", sheetRows: READ_ROWS });
            const out = parseRoster(book);
            if (out.error) setError(out.error); else setParsed(out);
        } catch {
            setError("We couldn't read that file. Upload an Excel (.xlsx or .xls) or CSV file.");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy || rows.length === 0 || tooMany) return; // same as the disabled Upload button
        setBusy(true);
        setError("");
        try {
            const res = await api.post(`/org/${orgId}/roster/import`, {
                rows: rows.map((r) => ({
                    firstName: r.firstName,
                    lastName: r.lastName,
                    rosterId: r.rosterId || undefined,
                    email: r.email || undefined,
                })),
                programId: programId ? Number(programId) : undefined,
                assignLicense,
            });
            setResult(res.data);
            await onDone();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    // The server numbers rows by their place in the upload; show the file's own line numbers
    const fileRow = (n) => rows[n - 1]?.sheetRow ?? n;
    const studentSeats = org.StudentLicenseLimit + (org.StudentLicensesPurchased || 0);
    const seatsLeft = Math.max(0, studentSeats - org.StudentLicensesUsed);
    const missing = parsed?.missing || [];
    // A later upload of the same file finds these people again by name only
    const noKey = rows.filter((r) => !r.rosterId && !r.email).length;
    const programName = programs.find((p) => String(p.ProgramID) === String(programId))?.ProgramName || "the program";

    return (
        <OrgModal
            isOpen
            wide
            onClose={onClose}
            title="Upload roster"
            footer={result
                ? <button type="button" className={s.btnPrimary} onClick={onClose}>Done</button>
                : <>
                    <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                    <button type="submit" form="roster-import-form" className={s.btnPrimary} disabled={busy || rows.length === 0 || tooMany}>
                        {/* old {busy && <Loader2 size={14} className="animate-spin" />} Upload {rows.length || ""} student{rows.length === 1 ? "" : "s"} */}
                        {busy && <Loader2 size={14} className="animate-spin" />} Upload {rows.length || ""} client{rows.length === 1 ? "" : "s"}
                    </button>
                </>}
        >
            <Alert>{error}</Alert>
            {result ? (
                <div className="space-y-3 text-sm">
                    <div>
                        <CountLine label="Added to your roster" count={result.added} tone="success" />
                        {/* added rows whose email is a student who already joined (one Joined row each) */}
                        {/* old {result.linked > 0 && <p className={`mt-1 ${s.muted}`}>{result.linked} matched to students who already joined.</p>} */}
                        {result.linked > 0 && <p className={`mt-1 ${s.muted}`}>{result.linked} matched to clients who already joined.</p>}
                    </div>
                    {assignLicense && (
                        <div>
                            <CountLine label="Given a license" count={result.licensed} tone="success" />
                            {/* new rows or ones already on the roster that had a license this month */}
                            {result.alreadyLicensed > 0 && <p className={`mt-1 ${s.muted}`}>{result.alreadyLicensed} already had a license.</p>}
                        </div>
                    )}
                    {result.notLicensed > 0 && (
                        <div>
                            <CountLine label="Not given a license" count={result.notLicensed} tone="warning" />
                            {result.licenseError && <p className={`mt-1 ${s.muted}`}>{result.licenseError}</p>}
                        </div>
                    )}
                    {/* someone listed twice points at their earlier row (sameAsRow), shown as its line in the
                        file; reason numbers it by its place in the upload */}
                    <ResultLine label="Already on your roster" items={(result.alreadyOnRoster || []).map((x) => (
                        `Row ${fileRow(x.row)} (${x.name}): ${x.sameAsRow ? `Same as row ${fileRow(x.sameAsRow)}` : x.reason}`))} />
                    {/* uploaded for a program: people already on the roster go in it too, except invited ones
                        waiting for another program (an invited student joins one program) */}
                    {result.enrolledExisting > 0 && (
                        <p className={s.muted}>
                            {result.enrolledExisting} already on your roster {result.enrolledExisting === 1 ? "was" : "were"} added to {programName}.
                        </p>
                    )}
                    {result.otherProgram?.length > 0 && (
                        <div>
                            <ResultLine label={`Not added to ${programName}: invited for another program`} tone="warning"
                                        items={result.otherProgram.map((x) => `Row ${fileRow(x.row)} (${x.name}): ${x.program}`)} />
                            <p className={`mt-1 ${s.muted}`}>They join that program when they join. Add them to {programName} after that.</p>
                        </div>
                    )}
                    <ResultLine label="Not added" items={(result.invalid || []).map((x) => `Row ${fileRow(x.row)}: ${x.error}`)} tone="danger" />
                    <ResultLine label="Skipped: no first or last name" items={missing.map((x) => `Row ${x.sheetRow}`)} tone="danger" />
                    {result.licensed > 0 && (
                        //old <p className={s.muted}>Each license has its own code. Use Download codes on the roster to get every student's code and join link.</p>
                        <p className={s.muted}>Each license has its own code. Use Download codes on the roster to get every client's code and join link.</p>
                    )}
                </div>
            ) : (
                <form id="roster-import-form" onSubmit={submit} className="space-y-4">
                    <div className={`rounded-xl border p-4 text-sm ${s.subPanel}`}>
                        <p className={s.body}>
                            An Excel or CSV file with a header row: <strong className={s.text}>First name</strong>, <strong className={s.text}>Last name</strong>,
                            {/* old and if you have them <strong className={s.text}>Roster ID</strong> (your own student ID) and <strong className={s.text}>Email</strong>. */}
                            {" "}and if you have them <strong className={s.text}>Roster ID</strong> (your own client ID) and <strong className={s.text}>Email</strong>.
                            {/* old Up to {MAX_IMPORT} students at a time. */}
                            {" "}Up to {MAX_IMPORT} clients at a time.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <label className={`inline-flex cursor-pointer items-center gap-2 ${s.btnSecondary}`}>
                                <Upload size={14} /> {fileName ? "Choose another file" : "Choose file"}
                                <input type="file" className="sr-only" onChange={onFile}
                                       accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv" />
                            </label>
                            <button type="button" className={s.btnSecondary} onClick={downloadRosterTemplate}>
                                <Download size={14} /> Download template
                            </button>
                        </div>
                    </div>

                    {parsed && (
                        <div className="space-y-2">
                            <p className={`flex items-center gap-2 text-sm ${s.body}`}>
                                <FileSpreadsheet size={16} className={s.muted} />
                                <span>
                                    <strong className={s.text}>{fileName}</strong>:{" "}
                                    {/* old {parsed.truncated ? "too many rows" : `${tooMany ? `more than ${MAX_IMPORT}` : rows.length} student${rows.length === 1 ? "" : "s"}`} */}
                                    {parsed.truncated ? "too many rows" : `${tooMany ? `more than ${MAX_IMPORT}` : rows.length} client${rows.length === 1 ? "" : "s"}`}
                                </span>
                            </p>
                            {tooMany && (
                                <p className={`text-xs font-medium ${s.dark ? "text-red-300" : "text-red-600"}`}>
                                    {parsed.truncated
                                        //old ? `This file goes on past row ${READ_ROWS.toLocaleString()}, even if those rows look empty. Delete the empty rows at the bottom, or copy the students into a new file.`
                                        ? `This file goes on past row ${READ_ROWS.toLocaleString()}, even if those rows look empty. Delete the empty rows at the bottom, or copy the clients into a new file.`
                                        //old : `Upload up to ${MAX_IMPORT} students at a time. Split the file and upload each part.`}
                                        : `Upload up to ${MAX_IMPORT} clients at a time. Split the file and upload each part.`}
                                </p>
                            )}
                            {/* the file column behind each field, so a missing one shows before uploading */}
                            <p className={`text-xs ${s.muted}`}>
                                {ROSTER_FIELDS.map(([field, label]) => `${label}: ${parsed.headers?.[field] || "not found in this file"}`).join(" · ")}
                            </p>
                            {(parsed.ignored || []).map((x) => (
                                <p key={x.label} className={`text-xs ${s.muted}`}>Not used for {x.label}: "{x.header}", because {x.reason}.</p>
                            ))}
                            {rows.length > 0 && (
                                <div className={`overflow-x-auto rounded-xl border ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                                    <table className="w-full">
                                        <thead>
                                            <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                                <th className={s.th}>First name</th>
                                                <th className={s.th}>Last name</th>
                                                <th className={s.th}>Roster ID</th>
                                                <th className={s.th}>Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.slice(0, 10).map((r) => (
                                                <tr key={r.sheetRow} className={`border-b last:border-0 ${s.row}`}>
                                                    <td className={s.td}>{r.firstName}</td>
                                                    <td className={s.td}>{r.lastName}</td>
                                                    <td className={s.td}>{r.rosterId || <span className={s.muted}>—</span>}</td>
                                                    <td className={s.td}>{r.email || <span className={s.muted}>—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {rows.length > 10 && !tooMany && <p className={`text-xs ${s.muted}`}>Showing the first 10 of {rows.length}.</p>}
                            {missing.length > 0 && (
                                <p className={`text-xs ${s.dark ? "text-amber-300" : "text-amber-700"}`}>
                                    {missing.length} row{missing.length === 1 ? " is" : "s are"} missing a first or last name and won't be uploaded:
                                    {" "}row{missing.length === 1 ? "" : "s"} {missing.slice(0, 12).map((r) => r.sheetRow).join(", ")}
                                    {missing.length > 12 ? ` and ${missing.length - 12} more` : ""}.
                                </p>
                            )}
                            {(parsed.repeats || []).length > 0 && (
                                <p className={`text-xs ${s.dark ? "text-amber-300" : "text-amber-700"}`}>
                                    {parsed.repeats.length} row{parsed.repeats.length === 1 ? " has" : "s have"} the same roster ID or email as an earlier row
                                    {/* old and will be treated as the same student: row{parsed.repeats.length === 1 ? "" : "s"} {parsed.repeats.slice(0, 12).join(", ")} */}
                                    {" "}and will be treated as the same client: row{parsed.repeats.length === 1 ? "" : "s"} {parsed.repeats.slice(0, 12).join(", ")}
                                    {parsed.repeats.length > 12 ? ` and ${parsed.repeats.length - 12} more` : ""}.
                                </p>
                            )}
                            {noKey > 0 && (
                                <p className={`text-xs ${s.muted}`}>
                                    {noKey} row{noKey === 1 ? " has" : "s have"} no roster ID or email. If you upload this file again,
                                    {noKey === 1 ? " it" : " they"} can only be matched by name.
                                </p>
                            )}
                        </div>
                    )}

                    <Field label="Add to program" htmlFor="roster-program">
                        <select id="roster-program" className={s.input} value={programId} onChange={(e) => setProgramId(e.target.value)}>
                            <option value="">Roster only (no program)</option>
                            {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                        </select>
                    </Field>
                    {studentSeats > 0 && (
                        <label className={`flex items-start gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" className="mt-1" checked={assignLicense} disabled={seatsLeft === 0}
                                   onChange={(e) => setAssignLicense(e.target.checked)} />
                            <span>
                                Give each a license ({seatsLeft} available)
                                <span className={`block text-xs ${s.muted}`}>
                                    Everyone in this file who doesn't have a license gets one, in file order, until licenses run out.
                                    {/* old A license counts this month from the moment it's given, whether or not the student joins. */}
                                    {" "}A license counts this month from the moment it's given, whether or not the client joins.
                                    {/* old Each license has a code the student enters to join. */}
                                    {" "}Each license has a code the client enters to join.
                                </span>
                            </span>
                        </label>
                    )}
                </form>
            )}
        </OrgModal>
    );
}

/** What Add client did, said on the page once its window closes. */
function clientAddedMessage(r, name, programName) {
    const parts = [r.added ? `Added ${name} (${r.email}).` : `${r.email} is already on your list.`];
    if (r.licensed) {
        parts.push(!r.emailed ? "License assigned."
            : r.joined ? "License assigned. It's on their account now, and we emailed them to let them know."
                : "License assigned. We emailed them a link to join.");
    } else if (r.alreadyLicensed) {
        parts.push("They already have a license this month, so none was used.");
    } else if (r.added && r.emailed) {
        // added without a license, they're emailed once too (JJ, Nov 2): how to join, and that they can upgrade their
        // own plan or wait for a license
        parts.push(r.joined ? "No license was given. We emailed them to let them know they were added."
            : "No license was given. We emailed them how to join: they sign up or log in with that email.");
    } else if (r.added) {
        // not joined yet: they join on their own once they're signed in with that email (JJ, Nov 2)
        parts.push(r.joined ? "No license was given, so we didn't email them."
            : "No license was given, so we didn't email them. They join automatically when they sign up with that email, or open My Coaching → Programs if they already have an account.");
    }
    // already invited for another program: an invited client joins one program (as the upload says)
    if (r.otherProgram) parts.push(`They're invited to ${r.otherProgram}, so they weren't added to ${programName || "this program"}.`);
    return parts.join(" ");
}

/**
 * Add client (Alicia, Oct 30: an organization with no roster adds its clients one at a time): first and last
 * name, email and a program, saved the way the upload saves a row. Both names are asked for, as the upload
 * asks for them (the roster lists people by name); the email is where their license goes. A license while
 * some are left (ticked by default) is emailed to them with their link to join (JJ, Nov 1); without one we
 * email them how to join (JJ, Nov 2). With none left the box is off, with the way to get more.
 */
function AddClientModal({ orgId, org, programs, defaultProgramId, onClose, onDone }) {
    const s = useOrgStyles();
    const { goTo, reloadOrg } = useOrgPortal();
    const seats = org.StudentLicenseLimit + (org.StudentLicensesPurchased || 0);
    const seatsLeft = Math.max(0, seats - org.StudentLicensesUsed);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [programId, setProgramId] = useState(defaultProgramId || "");
    const [assignLicense, setAssignLicense] = useState(seatsLeft > 0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const ready = !!(firstName.trim() && lastName.trim() && email.trim());
    const withLicense = assignLicense && seatsLeft > 0;

    const submit = async (e) => {
        e.preventDefault();
        if (busy || !ready) return; // same as the disabled Add client button
        setBusy(true);
        setError("");
        try {
            const res = await api.post(`/org/${orgId}/clients`, {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                programId: programId ? Number(programId) : undefined,
                assignLicense: withLicense,
            });
            const programName = programs.find((p) => String(p.ProgramID) === String(programId))?.ProgramName;
            await onDone(clientAddedMessage(res.data, `${firstName.trim()} ${lastName.trim()}`, programName));
            onClose();
        } catch (err) {
            setError(apiError(err));
            if (err?.response?.data?.code === "NO_SEATS") {
                // none left after all (another admin took the last one): the box goes off, what was typed stays
                setAssignLicense(false);
                await reloadOrg();
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <OrgModal
            isOpen
            onClose={onClose}
            title="Add a client"
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="add-client-form" className={s.btnPrimary} disabled={busy || !ready} aria-busy={busy}>
                    {busy ? "Adding…" : "Add client"}
                </button>
            </>}
        >
            <Alert>{error}</Alert>
            <form id="add-client-form" onSubmit={submit} noValidate className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="First name" htmlFor="client-first">
                        <input id="client-first" className={s.input} value={firstName} maxLength={100} autoComplete="off"
                               onChange={(e) => setFirstName(e.target.value)} autoFocus />
                    </Field>
                    <Field label="Last name" htmlFor="client-last">
                        <input id="client-last" className={s.input} value={lastName} maxLength={100} autoComplete="off"
                               onChange={(e) => setLastName(e.target.value)} />
                    </Field>
                </div>
                {/* the hint follows the license box: without a license we email them how to join (JJ, Nov 2: once, with
                    "upgrade your own plan or wait for a license" when the organization gives client licenses) */}
                <Field label="Email" htmlFor="client-email"
                       hint={withLicense ? "Their license and their link to join are emailed here."
                           : "We email them here how to join: they sign up or log in with this email."}>
                    <input id="client-email" type="email" inputMode="email" className={s.input} value={email} maxLength={255}
                           autoComplete="off" spellCheck={false} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label="Add to program" htmlFor="client-program">
                    <select id="client-program" className={s.input} value={programId} onChange={(e) => setProgramId(e.target.value)}>
                        <option value="">Roster only (no program)</option>
                        {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                    </select>
                </Field>
                {seats > 0 && (
                    <div>
                        <label className={`flex items-start gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" className="mt-1" checked={withLicense} disabled={seatsLeft === 0}
                                   onChange={(e) => setAssignLicense(e.target.checked)} />
                            <span>
                                Give them a license ({seatsLeft} available)
                                <span className={`block text-xs ${s.muted}`}>
                                    {seatsLeft === 0 ? "No client licenses left this month."
                                        : "We email them a link to join with it. It counts this month from the moment it's given."}
                                </span>
                            </span>
                        </label>
                        {seatsLeft === 0 && (
                            <button type="button" className={`mt-2 ${s.btnLink}`} onClick={() => goTo("licenses")}>Get more licenses</button>
                        )}
                    </div>
                )}
            </form>
        </OrgModal>
    );
}

function CountLine({ label, count, tone = "neutral" }) {
    const s = useOrgStyles();
    return <div className={`flex items-center gap-2 font-semibold ${s.text}`}>{label} <Pill tone={tone}>{count || 0}</Pill></div>;
}

function ResultLine({ label, items, tone = "neutral" }) {
    const s = useOrgStyles();
    if (!items?.length) return null;
    return (
        <div>
            <div className={`mb-1 flex items-center gap-2 font-semibold ${s.text}`}>{label} <Pill tone={tone}>{items.length}</Pill></div>
            <p className={`break-words ${s.muted}`}>{items.slice(0, 12).join(", ")}{items.length > 12 ? ` and ${items.length - 12} more` : ""}</p>
        </div>
    );
}

//old function StudentDetail({ orgId, clientId, isOrgAdmin, programs, onClose, onChanged }) {
function StudentDetail({ orgId, clientId, rosterEntryId, isOrgAdmin, programs, onClose, onChanged }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [license, setLicense] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [addProgramId, setAddProgramId] = useState("");
    const [analysis, setAnalysis] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/students/${clientId}`);
            setData(res.data);
            if (isOrgAdmin) {
                const lic = await api.get(`/org/${orgId}/licenses`);
                setLicense((lic.data.licenses || []).find((l) => l.ClientID === clientId) || null);
            }
        } catch (err) {
            //old setError(apiError(err, "Couldn't load this student."));
            setError(apiError(err, "Couldn't load this client."));
        }
    }, [orgId, clientId, isOrgAdmin]);

    useEffect(() => { load(); }, [load]);

    const run = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await Promise.all([load(), onChanged()]);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const openAnalysis = async (id) => {
        try {
            const res = await api.get(`/org/${orgId}/students/${clientId}/resume-analyses/${id}`);
            const a = res.data.analysis;
            let results = null;
            try { results = JSON.parse(a.ResultsJSON || "null"); } catch { results = null; }
            setAnalysis({ ...a, results });
        } catch (err) {
            setError(apiError(err));
        }
    };

    const student = data?.student;
    const enrolledIds = new Set((data?.programs || []).map((p) => p.ProgramID));
    const available = programs.filter((p) => !enrolledIds.has(p.ProgramID));
    // Their roster entry: the one their row had when it was opened, else their live
    // license's (a removed license can point at an entry that's gone, e.g. after they
    // left and joined again). Members who joined with a program code have none until
    // they're given a license.
    const member = { RosterEntryID: rosterEntryId || (license?.IsActive ? license.RosterEntryID : null) || null, ClientID: clientId };

    return (
        //old <OrgModal isOpen wide onClose={onClose} title={student ? `${student.FirstName} ${student.LastName}` : "Student"}>
        <OrgModal isOpen wide onClose={onClose} title={student ? `${student.FirstName} ${student.LastName}` : "Client"}>
            <Alert onClose={() => setError("")}>{error}</Alert>
            {!data ? (
                !error && <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
            ) : analysis ? (
                <AnalysisView analysis={analysis} onBack={() => setAnalysis(null)} />
            ) : (
                <div className="space-y-6">
                    <div className={`flex flex-wrap items-center justify-between gap-2 text-sm ${s.body}`}>
                        <span>{student.EmailAddress}</span>
                        <span className={s.muted}>Joined {formatDate(student.JoinedOn)}</span>
                    </div>

                    <section>
                        <h4 className={`mb-2 text-sm font-bold ${s.text}`}>Programs</h4>
                        {data.programs.length === 0 ? <p className={`text-sm ${s.muted}`}>Not in a program.</p> : (
                            <ul className="flex flex-wrap gap-2">
                                {data.programs.map((p) => (
                                    <li key={p.ProgramID} className={`inline-flex items-center gap-1 rounded-full border py-1 pl-3 pr-1 text-sm ${s.subPanel} ${s.body}`}>
                                        {p.ProgramName}
                                        {isOrgAdmin && (
                                            <button type="button" disabled={busy} aria-label={`Remove from ${p.ProgramName}`}
                                                    onClick={() => run(() => api.delete(`/org/${orgId}/students/${clientId}/programs/${p.ProgramID}`))}
                                                    className={`rounded-full p-1 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-200"}`}>
                                                <X size={12} />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {isOrgAdmin && available.length > 0 && (
                            <div className="mt-3 flex gap-2">
                                <select aria-label="Add to program" className={`${s.input} max-w-xs`} value={addProgramId} onChange={(e) => setAddProgramId(e.target.value)}>
                                    <option value="">Add to a program…</option>
                                    {available.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                                </select>
                                <button type="button" className={s.btnSecondary} disabled={!addProgramId || busy}
                                        onClick={() => run(async () => {
                                            await api.post(`/org/${orgId}/students/${clientId}/programs`, { programId: Number(addProgramId) });
                                            setAddProgramId("");
                                        })}>Add</button>
                            </div>
                        )}
                    </section>

                    {isOrgAdmin && (
                        <section className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${s.subPanel}`}>
                            <div className="flex items-center gap-2 text-sm">
                                <KeyRound size={16} className={s.muted} />
                                {license?.IsActive
                                    //old ? <span className={s.body}>Student license active this month.</span>
                                    ? <span className={s.body}>Client license active this month.</span>
                                    : license
                                        ? <span className={s.body}>License removed this month (the seat frees up on the 1st).</span>
                                        //old : <span className={s.body}>No student license.</span>}
                                        : <span className={s.body}>No client license.</span>}
                            </div>
                            {!!license?.IsActive && license.LicenseCode && <InviteCode code={license.LicenseCode} />}
                            {license?.IsActive ? (
                                <button type="button" className={s.btnDanger} disabled={busy}
                                        //old onClick={() => window.confirm("Remove this license? The student loses access now, and the seat can't be reassigned until next month.")
                                        onClick={() => window.confirm("Remove this license? The client loses access now, and the seat can't be reassigned until next month.")
                                            && run(() => api.delete(`/org/${orgId}/licenses/${license.LicenseID}`))}>
                                    Remove license
                                </button>
                            ) : (
                                <button type="button" className={s.btnSecondary} disabled={busy}
                                        //old onClick={() => run(() => api.post(`/org/${orgId}/licenses`, { licenseType: "student", clientId }))}>
                                        onClick={() => window.confirm(assignConfirm(student))
                                            && run(() => api.post(`/org/${orgId}/licenses`, licenseBody(member)))}>
                                    Assign license
                                </button>
                            )}
                        </section>
                    )}

                    <SharedSections data={data} onOpenAnalysis={openAnalysis} />

                    {isOrgAdmin && (
                        <div className={`flex justify-end border-t pt-4 ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                            <button type="button" className={s.btnDanger} disabled={busy}
                                    //old onClick={() => window.confirm("Remove this student from your organization? Their program enrollments end and any license is removed.")
                                    onClick={() => window.confirm("Remove this client from your organization? Their program enrollments end and any license is removed.")
                                        //old && run(async () => { await api.delete(`/org/${orgId}/students/${clientId}`); onClose(); })}>
                                        && run(async () => { await api.delete(removePath(orgId, member)); onClose(); })}>
                                Remove from organization
                            </button>
                        </div>
                    )}
                </div>
            )}
        </OrgModal>
    );
}

/** Someone on the roster who hasn't joined yet (no account): their details,
 *  license code, and the same actions as their roster row. */
function RosterEntryDetail({ orgId, entry, isOrgAdmin, onClose, onChanged }) {
    const s = useOrgStyles();
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const joined = entry.Status !== "invited";
    const programNames = (entry.Programs || []).map((p) => p.ProgramName).join(", ") || entry.ProgramName || "";

    const run = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await onChanged();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const fields = [
        ["First name", entry.FirstName],
        ["Last name", entry.LastName],
        ["Roster ID", entry.RosterID],
        ["Email", entry.Email],
        ["Program", programNames || "No program"],
    ];

    return (
        //old <OrgModal isOpen onClose={onClose} title={`${entry.FirstName} ${entry.LastName}`}>
        <OrgModal isOpen onClose={onClose} title={personName(entry)}>
            <Alert onClose={() => setError("")}>{error}</Alert>
            <div className="space-y-5">
                <dl className="grid gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
                    {fields.map(([label, value]) => (
                        <div key={label} className="min-w-0">
                            <dt className={`text-xs ${s.muted}`}>{label}</dt>
                            <dd className={`break-words ${value ? s.text : s.muted}`}>{value || "—"}</dd>
                        </div>
                    ))}
                    <div>
                        <dt className={`text-xs ${s.muted}`}>Status</dt>
                        <dd className="mt-0.5 flex flex-wrap items-center gap-2">
                            <Pill tone={joined ? "primary" : "neutral"}>{joined ? "Joined" : "Invited"}</Pill>
                            {!joined && <span className={s.muted}>Not joined yet</span>}
                        </dd>
                    </div>
                </dl>
                {/* how they join: someone with an email on their own, once they're signed in with it (JJ, Nov 2);
                    without one, only with their license code (the license box below) */}
                {!joined && entry.Email && (
                    <p className={`text-sm ${s.muted}`}>
                        They join automatically when they sign up with this email, or open My Coaching → Programs if they already have an account.
                    </p>
                )}

                {isOrgAdmin && (
                    <section className={`rounded-xl border p-4 ${s.subPanel}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-2 text-sm">
                                <KeyRound size={16} className={`mt-0.5 shrink-0 ${s.muted}`} />
                                <span className={s.body}>
                                    {/* old {!entry.HasLicense ? "No student license." */}
                                    {!entry.HasLicense ? "No client license."
                                        //old : entry.LicenseRedeemed ? "Student license active. The code has been used." : "Student license assigned. Not redeemed yet."}
                                        : entry.LicenseRedeemed ? "Client license active. The code has been used." : "Client license assigned. Not redeemed yet."}
                                </span>
                            </div>
                            {entry.HasLicense ? (
                                <button type="button" className={`${s.btnDanger} shrink-0 whitespace-nowrap`} disabled={busy}
                                        onClick={() => window.confirm("Remove this license? The code stops working now, and the license can't go to someone else until next month.")
                                            && run(() => api.delete(`/org/${orgId}/licenses/${entry.LicenseID}`))}>
                                    Remove license
                                </button>
                            ) : (
                                <button type="button" className={`${s.btnSecondary} shrink-0 whitespace-nowrap`} disabled={busy}
                                        onClick={() => window.confirm(assignConfirm(entry))
                                            && run(() => api.post(`/org/${orgId}/licenses`, licenseBody(entry)))}>
                                    Assign license
                                </button>
                            )}
                        </div>
                        {!!entry.HasLicense && entry.LicenseCode && (
                            <div className="mt-3">
                                <InviteCode code={entry.LicenseCode} />
                                <p className={`mt-2 text-xs ${s.muted}`}>
                                    {/* old Give the student this code or link. They enter it under My Coaching → Programs, or when they create their account. */}
                                    Give the client this code or link. They enter it under My Coaching → Programs, or when they create their account.
                                </p>
                            </div>
                        )}
                    </section>
                )}

                {isOrgAdmin && (
                    <div className={`flex justify-end border-t pt-4 ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                        <button type="button" className={s.btnDanger} disabled={busy}
                                onClick={() => window.confirm(removeConfirm(entry))
                                    && run(async () => { await api.delete(removePath(orgId, entry)); onClose(); })}>
                            Remove from roster
                        </button>
                    </div>
                )}
            </div>
        </OrgModal>
    );
}

function NotShared() {
    const s = useOrgStyles();
    return (
        <p className={`flex items-center gap-2 text-sm ${s.muted}`}>
            <Lock size={14} /> Not shared with your organization.
        </p>
    );
}

function SharedSections({ data, onOpenAnalysis }) {
    const s = useOrgStyles();
    if (!data.detailsAvailable) {
        return (
            <div className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={16} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                {/* old <p className={s.body}>Student details (resume reviews, quarterly results, program progress) are available to Enterprise organizations when the student chooses to share them.</p> */}
                <p className={s.body}>Client details (resume reviews, quarterly results, program progress) are available to Enterprise organizations when the client chooses to share them.</p>
            </div>
        );
    }
    return (
        <div className="space-y-6">
            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><FileText size={16} /> Resume reviews</h4>
                {!data.sharing.shareResume ? <NotShared /> : data.resume.analyses.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>No resume reviews yet.</p>
                ) : (
                    <ul className={`divide-y rounded-xl border ${s.divider} ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                        {data.resume.analyses.map((a) => (
                            <li key={a.AnalysisID}>
                                <button type="button" onClick={() => onOpenAnalysis(a.AnalysisID)}
                                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm ${s.dark ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}`}>
                                    <span className="min-w-0">
                                        <span className={`block truncate font-semibold ${s.text}`}>{a.Label || a.TargetJob || a.ResumeLabel}</span>
                                        <span className={`block text-xs ${s.muted}`}>
                                            {formatDate(a.CreatedOn)}{a.ProgramName ? ` · ${a.ProgramName} criteria` : ""}
                                        </span>
                                    </span>
                                    {a.OverallScore != null && <Pill tone={a.OverallScore >= 80 ? "success" : a.OverallScore >= 60 ? "warning" : "danger"}>{a.OverallScore}</Pill>}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><CalendarRange size={16} /> Quarterly results</h4>
                {!data.sharing.shareQuarterlyResults ? <NotShared /> : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {data.quarterlyResults.filter((q) => q.totalTasks > 0).length === 0
                            ? <p className={`col-span-full text-sm ${s.muted}`}>No quarterly goals yet.</p>
                            : data.quarterlyResults.filter((q) => q.totalTasks > 0).map((q) => (
                                <div key={q.QuarterID} className={`rounded-xl border p-3 ${s.subPanel}`}>
                                    <div className={`text-xs ${s.muted}`}>{q.QuarterName} {q.FiscalYear}</div>
                                    <div className={`text-lg font-bold tabular-nums ${s.text}`}>{q.completionRate}%</div>
                                    <div className={`text-xs ${s.muted}`}>{q.completedTasks} of {q.totalTasks} goals</div>
                                </div>
                            ))}
                    </div>
                )}
            </section>

            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><ClipboardCheck size={16} /> Program progress</h4>
                {!data.sharing.shareProgramProgress ? <NotShared /> : data.programProgress.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>Not in a program.</p>
                ) : (
                    <div className="space-y-3">
                        {data.programProgress.map((p) => {
                            const program = data.programs.find((x) => x.ProgramID === p.ProgramID);
                            return (
                                <div key={p.ProgramID} className={`rounded-xl border p-4 ${s.subPanel}`}>
                                    <div className={`mb-2 font-semibold ${s.text}`}>{program?.ProgramName}</div>
                                    <div className={`grid gap-2 text-sm sm:grid-cols-3 ${s.body}`}>
                                        <span>Assessments: <strong>{p.assessmentsCompleted}/{p.assessmentsTotal}</strong></span>
                                        <span>Resume reviews: <strong>{p.resumeEvaluations?.Count || 0}</strong>{p.resumeEvaluations?.BestScore != null && ` (best ${p.resumeEvaluations.BestScore})`}</span>
                                        <span>Mock interviews: <strong>{p.interviewSessions?.Count || 0}</strong></span>
                                    </div>
                                    {p.assessments.length > 0 && (
                                        <ul className="mt-2 flex flex-wrap gap-1">
                                            {p.assessments.map((a) => <Pill key={a.SurveyID} tone={a.Completed ? "success" : "neutral"}>{a.Title}</Pill>)}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

function AnalysisView({ analysis, onBack }) {
    const s = useOrgStyles();
    const r = analysis.results || {};
    return (
        <div className="space-y-4">
            {/* old <button type="button" className={s.btnLink} onClick={onBack}>← Back to student</button> */}
            <button type="button" className={s.btnLink} onClick={onBack}>← Back to client</button>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className={`font-bold ${s.text}`}>{analysis.Label || r.targetJob || "Resume review"}</div>
                    <div className={`text-xs ${s.muted}`}>{formatDate(analysis.CreatedOn)}{r.targetJob ? ` · ${r.targetJob}` : ""}</div>
                </div>
                {r.overallScore != null && <div className={`text-3xl font-extrabold tabular-nums ${s.text}`}>{r.overallScore}<span className={`text-base ${s.muted}`}>/100</span></div>}
            </div>
            {r.summary && <p className={`text-sm ${s.body}`}>{r.summary}</p>}
            <ul className="space-y-3">
                {(r.categories || []).map((c) => (
                    <li key={c.name} className={`rounded-xl border p-3 ${s.subPanel}`}>
                        <div className="flex items-center justify-between gap-2 text-sm">
                            <span className={`font-semibold ${s.text}`}>{c.name}</span>
                            <span className={`tabular-nums ${s.body}`}>{c.score}{c.grade ? ` · ${c.grade}` : ""}</span>
                        </div>
                        {c.justification && <p className={`mt-1 text-xs ${s.muted}`}>{c.justification}</p>}
                    </li>
                ))}
            </ul>
            {r.topRecommendation && (
                <p className={`rounded-xl border p-3 text-sm ${s.subPanel} ${s.body}`}><strong>Top recommendation: </strong>{r.topRecommendation}</p>
            )}
        </div>
    );
}
